import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Archive, ClipboardList, ImageIcon, Search } from "lucide-react";
import {
  listQuestionnaireOverview,
  setQuestionnaireArchived,
  type OverviewRow,
} from "@/lib/questionnaire-admin.functions";
import { listClients } from "@/lib/customers.functions";
import { listCredentialHandoverInbox } from "@/lib/credential-handovers.functions";
import { warmAdminQuery } from "@/lib/admin-loader";

import {
  credentialDisplayName,
  credentialStatusLabel,
} from "@/lib/credential-handovers.shared";

export const Route = createFileRoute("/_admin/admin/questionnaires/")({
  head: () => ({
    meta: [
      { title: "Fragebögen – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  // Ticket AD4a: Sprungziele aus dem Arbeitsvorrat.
  validateSearch: (search: Record<string, unknown>): { status?: string; offen?: "1"; zugangsdaten?: "offen" } => ({
    status: typeof search.status === "string" ? search.status : undefined,
    offen: search.offen === "1" ? ("1" as const) : undefined,
    zugangsdaten: search.zugangsdaten === "offen" ? ("offen" as const) : undefined,
  }),
  loader: ({ context }) => {
    warmAdminQuery(context.queryClient, {
      queryKey: ["admin-questionnaires", { clientId: "", type: "", status: "", search: "" }],
      queryFn: () =>
        listQuestionnaireOverview({
          data: { clientId: null, type: null, status: null, search: null },
        }),
    });
    warmAdminQuery(context.queryClient, {
      queryKey: ["admin-clients-simple"],
      queryFn: () => listClients({}),
      staleTime: 60_000,
    });
  },
  component: QuestionnairesOverviewPage,
});


const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const TYPE_LABELS: Record<string, string> = {
  personaldatenblatt: "Personaldatenblatt",
  wahlkampf: "Wahlkampf",
  ttpa: "TTPA",
  design: "Design",
  design_standard: "Standard-Kampagnendesign",
  test: "Test",
};

const VARIANT_LABELS: Record<string, string> = {
  bundestagswahl: "Bundestagswahl",
  landtagswahl: "Landtagswahl",
  landratswahl: "Landratswahl",
  buergermeisterwahl: "Bürgermeisterwahl",
  oberbuergermeisterwahl: "Oberbürgermeisterwahl",
  kommunalwahl_direktmandat: "Kommunalwahl (Direktmandat)",
  europawahl: "Europawahl",
};

function statusBadge(status: OverviewRow["status"]) {
  switch (status) {
    case "completed":
      return { label: "Abgeschlossen", cls: "bg-brand-wahlen/15 text-brand-wahlen" };
    case "in_progress":
      return { label: "In Bearbeitung", cls: "bg-primary/10 text-primary" };
    default:
      return { label: "Nicht begonnen", cls: "bg-secondary text-muted-foreground" };
  }
}

function QuestionnairesOverviewPage() {
  const routeSearch = Route.useSearch();
  const listFn = useServerFn(listQuestionnaireOverview);
  const clientsFn = useServerFn(listClients);

  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [status, setStatus] = useState<string>(routeSearch.status ?? "");
  // Nur Rückläufe ohne Nachbearbeitung zeigen (Sprungziel aus dem Arbeitsvorrat).
  const [onlyUnreviewed, setOnlyUnreviewed] = useState(routeSearch.offen === "1");
  // Archivsicht: standardmäßig nur aktive Einträge.
  const [archivedView, setArchivedView] = useState<"active" | "archived" | "all">("active");
  const [selected, setSelected] = useState<string[]>([]);

  const { data: clients } = useQuery({
    queryKey: ["admin-clients-simple"],
    queryFn: () => clientsFn({}),
    staleTime: 60_000,
  });

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-questionnaires", { clientId, type, status, search, archivedView }],
    queryFn: () =>
      listFn({
        data: {
          clientId: clientId || null,
          type: (type || null) as any,
          status: (status || null) as any,
          search: search.trim() || null,
          archived: archivedView,
        },
      }),
    staleTime: 30_000,
  });

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    return onlyUnreviewed ? all.filter((r) => !r.reviewedAt) : all;
  }, [data, onlyUnreviewed]);

  const archiveFn = useServerFn(setQuestionnaireArchived);
  const archiveMut = useMutation({
    mutationFn: (vars: { ids: string[]; archived: boolean }) =>
      archiveFn({ data: { assignmentIds: vars.ids, archived: vars.archived } }),
    onSuccess: (_res, vars) => {
      toast.success(
        vars.archived
          ? `${vars.ids.length} Eintrag/Einträge archiviert.`
          : `${vars.ids.length} Eintrag/Einträge zurückgeholt.`,
      );
      setSelected([]);
      void refetch();
    },
    onError: (err: any) => toast.error(err?.message ?? "Aktion fehlgeschlagen."),
  });

  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.assignmentId));
  const toggleAll = (on: boolean) => setSelected(on ? rows.map((r) => r.assignmentId) : []);
  const toggleOne = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-primary">Fragebögen</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Statusübersicht aller Kunden-Fragebögen. Öffnen Sie einen Eintrag, um Antworten
            einzusehen oder als PDF/CSV zu exportieren. Jede Einsichtnahme wird protokolliert.
            Hier stehen die Rückläufe; die Fragebogen-Vorlagen selbst werden derzeit über die
            Agentur gepflegt.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Link
            to="/admin/questionnaires/bilder"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 font-display text-sm font-extrabold text-primary hover:bg-secondary"
          >
            <ImageIcon className="h-3.5 w-3.5" aria-hidden /> Bild-Optionen
          </Link>
          <ClipboardList className="h-8 w-8 text-primary" aria-hidden />
        </div>
      </div>


      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kunde oder Fragebogen…"
            className="w-64 rounded-md border border-border bg-background pl-7 pr-2 py-1.5 text-xs font-medium text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold text-foreground"
        >
          <option value="">Alle Kunden</option>
          {(clients ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.organization_name}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold text-foreground"
        >
          <option value="">Alle Typen</option>
          <option value="personaldatenblatt">Personaldatenblatt</option>
          <option value="wahlkampf">Wahlkampf</option>
          <option value="ttpa">TTPA</option>
          <option value="design">Design</option>
          <option value="design_standard">Standard-Kampagnendesign</option>
          <option value="test">Test</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold text-foreground"
        >
          <option value="">Alle Status</option>
          <option value="not_started">Nicht begonnen</option>
          <option value="in_progress">In Bearbeitung</option>
          <option value="completed">Abgeschlossen</option>
        </select>
        <select
          value={archivedView}
          onChange={(e) => {
            setArchivedView(e.target.value as "active" | "archived" | "all");
            setSelected([]);
          }}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold text-foreground"
        >
          <option value="active">Aktive Einträge</option>
          <option value="archived">Archiv</option>
          <option value="all">Aktive und Archiv</option>
        </select>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="ml-auto rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary disabled:opacity-60"
        >
          {isFetching ? "Aktualisiere…" : "Aktualisieren"}
        </button>
      </div>

      {selected.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs">
          <span className="font-semibold text-foreground">
            {selected.length} ausgewählt
          </span>
          <button
            type="button"
            onClick={() => archiveMut.mutate({ ids: selected, archived: true })}
            disabled={archiveMut.isPending}
            className="rounded-md border border-border bg-background px-2.5 py-1 font-semibold hover:bg-secondary disabled:opacity-60"
          >
            Archivieren
          </button>
          <button
            type="button"
            onClick={() => archiveMut.mutate({ ids: selected, archived: false })}
            disabled={archiveMut.isPending}
            className="rounded-md border border-border bg-background px-2.5 py-1 font-semibold hover:bg-secondary disabled:opacity-60"
          >
            Aus dem Archiv holen
          </button>
          <button
            type="button"
            onClick={() => setSelected([])}
            className="text-muted-foreground underline"
          >
            Auswahl aufheben
          </button>
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="bg-secondary/60 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="w-8 px-3 py-2.5">
                <input
                  type="checkbox"
                  aria-label="Alle sichtbaren Einträge auswählen"
                  checked={allSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
                />
              </th>
              <th className="px-3 py-2.5">Kunde</th>
              <th className="px-3 py-2.5">Fragebogen</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Fortschritt</th>
              <th className="px-3 py-2.5">Letzte Aktivität</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  Lade Fragebögen…
                </td>
              </tr>
            )}
            {error && !isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-destructive">
                  Konnte Fragebögen nicht laden.
                </td>
              </tr>
            )}
            {!isLoading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  Keine Fragebögen gefunden.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const badge = statusBadge(r.status);
              // Ticket K8: Ausfüllgrad über alle sichtbaren Fragen, zentral berechnet.
              const progress = r.progress;
              const isArchived = Boolean(r.archivedAt);
              return (
                <tr
                  key={`${r.assignmentId}`}
                  className={"align-top " + (isArchived ? "bg-secondary/30 opacity-70" : "")}
                >
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label={`${r.clientName} – ${r.templateTitle} auswählen`}
                      checked={selected.includes(r.assignmentId)}
                      onChange={() => toggleOne(r.assignmentId)}
                      className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-foreground">{r.clientName}</div>
                    {r.clientNumber && (
                      <div className="text-[11px] font-mono text-muted-foreground">
                        {r.clientNumber}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-foreground">{r.templateTitle}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {TYPE_LABELS[r.templateType] ?? r.templateType}
                      {r.templateVariant
                        ? ` · ${VARIANT_LABELS[r.templateVariant] ?? r.templateVariant}`
                        : ""}
                    </div>
                    {isArchived && (
                      <div className="mt-1 inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
                        <Archive className="h-3 w-3" aria-hidden /> Archiviert am{" "}
                        {dateFmt.format(new Date(r.archivedAt as string))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={"inline-flex rounded px-2 py-0.5 text-xs font-semibold " + badge.cls}
                    >
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-secondary">
                        <div
                          className="h-full bg-primary"
                          style={{ width: `${Math.round(progress * 100)}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-muted-foreground">
                        {Math.round(progress * 100)}% ·{" "}
                        {`${r.answeredQuestions}/${r.totalQuestions} Fragen`}
                        {r.status === "completed" ? " · abgeschlossen" : ""}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                    {r.lastActivity ? dateFmt.format(new Date(r.lastActivity)) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {r.responseId ? (
                        <Link
                          to="/admin/questionnaires/$responseId"
                          params={{ responseId: r.responseId }}
                          className="rounded-md border border-border px-2.5 py-1 text-xs font-semibold hover:bg-secondary"
                        >
                          Öffnen
                        </Link>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">
                          Noch keine Antworten
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          archiveMut.mutate({ ids: [r.assignmentId], archived: !isArchived })
                        }
                        disabled={archiveMut.isPending}
                        className="rounded-md border border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:bg-secondary disabled:opacity-60"
                      >
                        {isArchived ? "Aus dem Archiv holen" : "Archivieren"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Sortierung nach letzter Aktivität. Jede Einsichtnahme und jeder Export werden im
        Audit-Log gespeichert.
      </p>

      <CredentialHandoverInbox onlyOpen={routeSearch.zugangsdaten === "offen"} />
    </div>
  );
}

function CredentialHandoverInbox({ onlyOpen = false }: { onlyOpen?: boolean }) {
  const inboxFn = useServerFn(listCredentialHandoverInbox);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "credential-handover-inbox"],
    queryFn: () => inboxFn(),
    staleTime: 30_000,
  });

  const allRows = data?.rows ?? [];
  // Sprungziel „offene Zugangsdaten": abgelaufene Übergaben ausblenden.
  const rows = onlyOpen ? allRows.filter((r) => r.status !== "expired") : allRows;

  return (
    <section className="mt-12" id="zugangsdaten">
      <h2 className="font-display text-xl font-extrabold text-primary">
        Zugangsdaten-Übergaben
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Eingänge aus dem Cockpit. Die Inhalte werden ausschließlich auf der Kunden-Detailseite
        angezeigt – dort genau einmal pro Person.
      </p>

      <div className="mt-4 overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-secondary/60 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5">Kunde</th>
              <th className="px-3 py-2.5">Kategorie</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Eingang</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                  Lade Übergaben…
                </td>
              </tr>
            )}
            {error && !isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-destructive">
                  Konnte Übergaben nicht laden.
                </td>
              </tr>
            )}
            {!isLoading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                  Keine Zugangsdaten-Übergaben vorhanden.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="align-top">
                <td className="px-3 py-2.5 font-semibold text-foreground">{row.clientName}</td>
                <td className="px-3 py-2.5">
                  {credentialDisplayName(row.category, row.categoryLabel)}
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={
                      "inline-flex rounded px-2 py-0.5 text-xs font-semibold " +
                      (row.status === "expired"
                        ? "bg-secondary text-muted-foreground"
                        : row.status === "viewed"
                          ? "bg-brand-wahlen/15 text-brand-wahlen"
                          : "bg-primary/10 text-primary")
                    }
                  >
                    {credentialStatusLabel(row)}
                  </span>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                  {dateFmt.format(new Date(row.createdAt))}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <Link
                    to="/admin/clients/$id"
                    params={{ id: row.clientId }}
                    className="rounded-md border border-border px-2.5 py-1 text-xs font-semibold hover:bg-secondary"
                  >
                    Zum Kunden
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
